// ============================================================
// i18n — Multi-language support
// "I'm legally obligated to say EXPLOSIONS!" — Mr. Torgue (in every language)
// ============================================================

import type { LanguageConfig } from '../game/types';

// ==================== LANGUAGE CONFIGS ====================

export const LANGUAGES: Record<string, LanguageConfig> = {
  fr: {
    code: 'fr',
    name: 'Français',
    vowels: ['A', 'E', 'I', 'O', 'U'],
    letterWeights: {
      E: 1210, A: 711, I: 659, S: 651, N: 639, R: 607, T: 592,
      O: 502, L: 496, U: 449, D: 367, C: 318, M: 262, P: 249,
      G: 123, B: 114, V: 111, H: 111, F: 111, Q: 65,
      Y: 46, X: 38, J: 34, K: 29, W: 17, Z: 15,
    },
    letterPoints: {
      A: 1, B: 3, C: 3, D: 2, E: 1, F: 4, G: 2, H: 4, I: 1,
      J: 8, K: 10, L: 1, M: 2, N: 1, O: 1, P: 3, Q: 8, R: 1,
      S: 1, T: 1, U: 1, V: 4, W: 10, X: 10, Y: 10, Z: 10,
    },
    dictionaryUrl: 'https://raw.githubusercontent.com/words/an-array-of-french-words/master/index.json',
    localDictPath: '/dictionaries/fr.json',
  },
  en: {
    code: 'en',
    name: 'English',
    vowels: ['A', 'E', 'I', 'O', 'U'],
    letterWeights: {
      E: 1270, T: 906, A: 817, O: 751, I: 697, N: 675, S: 633,
      H: 609, R: 599, D: 425, L: 403, C: 278, U: 276, M: 241,
      W: 236, F: 223, G: 202, Y: 197, P: 193, B: 129,
      V: 98, K: 77, J: 15, X: 15, Q: 10, Z: 7,
    },
    letterPoints: {
      A: 1, B: 3, C: 3, D: 2, E: 1, F: 4, G: 2, H: 4, I: 1,
      J: 8, K: 5, L: 1, M: 3, N: 1, O: 1, P: 3, Q: 10, R: 1,
      S: 1, T: 1, U: 1, V: 4, W: 4, X: 8, Y: 4, Z: 10,
    },
    dictionaryUrl: 'https://raw.githubusercontent.com/words/an-array-of-english-words/master/index.json',
    localDictPath: '/dictionaries/en.json',
  },
};

// ==================== UI TRANSLATIONS ====================

const translations: Record<string, Record<string, string>> = {
  fr: {
    title: 'WORD DOGS',
    subtitle: 'Forge des mots. Domine le seau.',
    play: 'JOUER',
    retry: 'REJOUER',
    menu: 'MENU',
    profile: 'MON PROFIL',
    login: 'Connexion',
    signup: 'Inscription',
    loginBtn: 'SE CONNECTER',
    signupBtn: "S'INSCRIRE",
    skipAuth: 'Jouer sans compte',
    noAccount: 'Pas de compte ?',
    createAccount: 'Créer un compte',
    hasAccount: 'Déjà un compte ?',
    loginLink: 'Se connecter',
    logout: 'Déconnexion',
    email: 'Email',
    password: 'Mot de passe',
    passwordHint: 'Mot de passe (6+ caractères)',
    username: "Nom d'utilisateur",
    highScore: 'Meilleur score',
    score: 'SCORE',
    level: 'NIVEAU',
    gameOver: 'GAME OVER',
    best: 'Meilleur',
    words: 'Mots',
    bestWord: 'Meilleur mot',
    profileTitle: 'Mon Profil',
    bestScore: 'Meilleur Score',
    gamesPlayed: 'Parties Jouées',
    wordsFound: 'Mots Trouvés',
    history: 'Historique',
    back: 'RETOUR',
    loading: 'Chargement...',
    noGames: 'Aucune partie jouée',
    historyFail: "Impossible de charger l'historique",
    wordsUnit: 'mots',
    combo: 'COMBO',
    multiplayer: 'MULTIJOUEUR',
    findMatch: 'TROUVER UN MATCH',
    waiting: 'En attente...',
    vs: 'VS',
    youWin: 'VICTOIRE !',
    youLose: 'DÉFAITE',
    busted: 'a explosé son seau',
    youBusted: 'votre seau a débordé',
    language: 'Langue',
    replay: 'REVOIR',
    difficulty: 'Difficulté',
    diffEgoFriendly: 'Ego-friendly',
    diffEasy: 'Facile',
    diffNormal: 'Normal',
    diffHard: 'Difficile',
    modeSolo: 'SOLO',
    modeAI: 'CONTRE IA',
    modeLocalDuel: 'DUEL LOCAL',
    modeOnline: 'EN LIGNE',
    p1: 'J1', p2: 'J2',
    aiLabel: 'IA',
    quit: 'QUITTER',
    quitConfirm: 'Quitter la partie ?',
    pawSlash: 'Coup de patte',
    perkFreeze: 'Gel — fige le terrain adverse 3s',
    perkBlackout: 'Black-out — masque les lettres adverses 4s',
    perkScramble: 'Mélange — randomise les lettres adverses',
    perkFlood: 'Déluge — 8 balles dans le seau adverse',
    callBs: 'Bug suspect — capturer pour debug',
    bsCaptured: 'Bug signalé',
    bsSent: 'Rapport envoyé',
    createRoom: 'CRÉER UN SALON',
    joinRoom: 'REJOINDRE UN SALON',
    enterCode: 'Code du salon',
    codeLabel: 'Code',
    shareCode: 'Partage ce code',
    waitingPlayers: "En attente d'autres joueurs…",
    startMatch: 'LANCER LA PARTIE',
    needTwoPlayers: 'Au moins 2 joueurs requis',
    roomFull: 'Salon plein',
    roomNotFound: 'Salon introuvable',
    roomStarted: 'Partie déjà commencée',
    invalidCode: 'Code invalide',
    leaveRoom: 'QUITTER LE SALON',
    you: '(vous)',
    host: 'hôte',
    playersCount: 'Joueurs',
    yourName: 'Votre pseudo',
    gameMode: 'Mode',
    modeStandard: 'Standard',
    modeMiroir: 'Miroir',
    miroirHint: 'Tout le monde voit le même seau — premier mot trouvé gagne',
    wordStolen: 'Mot volé !',
  },
  en: {
    title: 'WORD DOGS',
    subtitle: 'Forge words. Dominate the bucket.',
    play: 'PLAY',
    retry: 'RETRY',
    menu: 'MENU',
    profile: 'MY PROFILE',
    login: 'Login',
    signup: 'Sign Up',
    loginBtn: 'LOG IN',
    signupBtn: 'SIGN UP',
    skipAuth: 'Play as guest',
    noAccount: 'No account?',
    createAccount: 'Create one',
    hasAccount: 'Already have an account?',
    loginLink: 'Log in',
    logout: 'Logout',
    email: 'Email',
    password: 'Password',
    passwordHint: 'Password (6+ characters)',
    username: 'Username',
    highScore: 'High score',
    score: 'SCORE',
    level: 'LEVEL',
    gameOver: 'GAME OVER',
    best: 'Best',
    words: 'Words',
    bestWord: 'Best word',
    profileTitle: 'My Profile',
    bestScore: 'Best Score',
    gamesPlayed: 'Games Played',
    wordsFound: 'Words Found',
    history: 'History',
    back: 'BACK',
    loading: 'Loading...',
    noGames: 'No games played',
    historyFail: 'Failed to load history',
    wordsUnit: 'words',
    combo: 'COMBO',
    multiplayer: 'MULTIPLAYER',
    findMatch: 'FIND MATCH',
    waiting: 'Waiting...',
    vs: 'VS',
    youWin: 'YOU WIN!',
    youLose: 'DEFEAT',
    busted: 'busted their bucket',
    youBusted: 'your bucket overflowed',
    language: 'Language',
    replay: 'REPLAY',
    difficulty: 'Difficulty',
    diffEgoFriendly: 'Ego-friendly',
    diffEasy: 'Easy',
    diffNormal: 'Normal',
    diffHard: 'Hard',
    modeSolo: 'SOLO',
    modeAI: 'VS AI',
    modeLocalDuel: 'LOCAL DUEL',
    modeOnline: 'ONLINE',
    p1: 'P1', p2: 'P2',
    aiLabel: 'AI',
    quit: 'QUIT',
    quitConfirm: 'Quit the game?',
    pawSlash: 'Paw slash',
    perkFreeze: 'Freeze — halt opponents 3s',
    perkBlackout: 'Blackout — hide opponent letters 4s',
    perkScramble: 'Scramble — randomize opponent letters',
    perkFlood: 'Flood — 8 balls into opponent bucket',
    callBs: 'Call BS — flag suspected bug',
    bsCaptured: 'Bug reported',
    bsSent: 'Report sent',
    createRoom: 'CREATE ROOM',
    joinRoom: 'JOIN ROOM',
    enterCode: 'Room code',
    codeLabel: 'Code',
    shareCode: 'Share this code',
    waitingPlayers: 'Waiting for more players…',
    startMatch: 'START MATCH',
    needTwoPlayers: 'At least 2 players required',
    roomFull: 'Room full',
    roomNotFound: 'Room not found',
    roomStarted: 'Game already started',
    invalidCode: 'Invalid code',
    leaveRoom: 'LEAVE ROOM',
    you: '(you)',
    host: 'host',
    playersCount: 'Players',
    yourName: 'Your name',
    gameMode: 'Mode',
    modeStandard: 'Standard',
    modeMiroir: 'Mirror',
    miroirHint: 'Everyone sees the same bucket — first word wins',
    wordStolen: 'Word stolen!',
  },
};

// ==================== STATE ====================

let currentLang = localStorage.getItem('wardogs_lang') || 'fr';

export function getLang(): string {
  return currentLang;
}

export function setLang(code: string) {
  if (LANGUAGES[code]) {
    currentLang = code;
    localStorage.setItem('wardogs_lang', code);
  }
}

export function t(key: string): string {
  return translations[currentLang]?.[key] ?? translations.fr[key] ?? key;
}

export function getLangConfig(): LanguageConfig {
  return LANGUAGES[currentLang] || LANGUAGES.fr;
}
